// COMPONENTS & FUNCTIONS
import { calculateTankEmptying } from "../calculations/tankEmptying";

// DATA
import { Acceleration } from "../baseQuantities/acceleration";
import { Area } from "../baseQuantities/area";
import { Flow } from "../baseQuantities/flow";
import { Length } from "../baseQuantities/length";
import { Time } from "../baseQuantities/time";
import { UnitLess } from "../baseQuantities/unitLess";

// SVG & IMAGES
import tankEmptyingFigure from "../../img/calc-figure-tank-emptying.svg";

// INTERFACES
import {
  AdvancedCalculator,
  AppData,
} from "../../interface/functionalityInterface";

/*
TANK EMPTYING CALCULATOR INFORMATION
  Inputs and outputs are stored in their coherent SI unit before calculation.
  The displayed unit can therefore change without changing the calculation.
  The calculator model assumes a vertical cylindrical tank with a circular
  outlet and uses Torricelli's law with a discharge coefficient.
*/

export const tankEmptyingCalc: AdvancedCalculator = {
  type: AppData.ADVANCED_CALCULATOR,
  definitionId: "tank-emptying",
  updatedAt: "2026-07-27",
  name: "tank emptying",
  branch: "Fluids",
  difficulty: "complex",
  equation: "t = 2A₁√H₀ / (Cd·A₂√(2g))",
  figure: tankEmptyingFigure,
  inputs: [
    {
      ...Length,
      id: "tank-diameter",
      name: "tank_diameter",
      type: "numeric",
      value: "1000",
      symbol: "Di₁",
      selectedUnit: "mm",
    },
    {
      ...Length,
      id: "outlet-diameter",
      name: "outlet_diameter",
      type: "numeric",
      value: "48.6",
      symbol: "Di₂",
      selectedUnit: "mm",
    },
    {
      ...Length,
      id: "initial-level",
      name: "starting_liquid_level",
      type: "numeric",
      value: "2000",
      symbol: "H₀",
      selectedUnit: "mm",
    },
    {
      ...UnitLess,
      id: "discharge-coefficient",
      name: "discharge_coefficient",
      value: "0.94",
      clearValue: "0.94",
      symbol: "Cd",
    },
    {
      ...Acceleration,
      id: "gravity",
      name: "gravity",
      value: "9.81",
      clearValue: "9.81",
      symbol: "g",
    },
  ],
  outputs: [
    {
      ...Time,
      id: "emptying-time",
      name: "emptying_time",
      selectedUnit: "min",
    },
    {
      ...Flow,
      id: "initial-flow",
      name: "initial_flow",
      selectedUnit: "m³/h",
    },
    {
      ...Area,
      id: "tank-area",
      name: "tank_area",
      decimalsOffset: 4,
    },
    {
      ...Area,
      id: "outlet-area",
      name: "outlet_area",
      decimalsOffset: 4,
    },
  ],
  calculate: (inputs) => {
    const result = calculateTankEmptying({
      tankDiameterMm: inputs.tank_diameter * 1000,
      outletDiameterMm: inputs.outlet_diameter * 1000,
      initialLevelMm: inputs.starting_liquid_level * 1000,
      dischargeCoefficient: inputs.discharge_coefficient,
      gravity: inputs.gravity,
    });

    if (!result) return null;

    return {
      outputs: {
        emptying_time: result.emptyingTimeSeconds,
        initial_flow: result.initialFlowM3PerHour / 3600,
        tank_area: result.tankAreaM2,
        outlet_area: result.outletAreaM2,
      },
      series: result.points.map((point) => ({
        time: point.timeSeconds,
        liquid_level: point.liquidLevelMm / 1000,
        outlet_flow: point.flowM3PerHour / 3600,
      })),
    };
  },
  validate: (inputs) =>
    inputs.outlet_diameter >= inputs.tank_diameter
      ? ["The outlet should normally be smaller than the tank."]
      : [],
  chart: {
    title: "Liquid level and outlet flow over time",
    xAxis: {
      key: "time",
      label: "Time",
      baseUnit: Time.baseUnit,
      selectedUnit: Time.selectedUnit,
      units: Time.units,
    },
    datasets: [
      {
        key: "liquid_level",
        label: "Liquid level",
        axisId: "level",
        baseUnit: Length.baseUnit,
        selectedUnit: Length.selectedUnit,
        selectedUnitFromInput: "starting_liquid_level",
        units: Length.units,
        borderColor: "#001e1d",
      },
      {
        key: "outlet_flow",
        label: "Outlet flow",
        axisId: "flow",
        baseUnit: Flow.baseUnit,
        selectedUnit: Flow.selectedUnit,
        units: Flow.units,
        borderColor: "#777",
        borderDash: [5, 4],
      },
    ],
  },
};
